import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { AppContext, DEFAULT_STATS } from '../AppContext';
import { auth } from '../Firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import {
  Plus, Trash2, Edit3, LogIn, Lock, Mail, ShoppingBag, Gift,
  FileText, MessageSquare, LogOut, Search, Eye, EyeOff, ShieldCheck, X, ExternalLink, RefreshCw,
  LayoutDashboard, TrendingUp, Layers, Clock, ArrowRight, CheckCircle2, BarChart2, Sparkles, PackageCheck,
  Upload, Filter, Calendar, ChevronLeft, ChevronRight, PhoneCall, ThumbsUp, ThumbsDown,
  Gem, Award, Users, Star, Heart, Save, RotateCcw
} from 'lucide-react';
import './AdminPanel.css';
import { showSuccessAlert, showErrorAlert, showConfirmDialog } from '../sweetAlert';

const STAT_ICONS = [
  { id: 'Gem', label: 'Diamond / Gem', icon: Gem },
  { id: 'Clock', label: 'Clock / Years', icon: Clock },
  { id: 'Users', label: 'Users / Clients', icon: Users },
  { id: 'Award', label: 'Award / Purity', icon: Award },
  { id: 'Sparkles', label: 'Sparkles / Artistry', icon: Sparkles },
  { id: 'Heart', label: 'Heart / Trust', icon: Heart },
  { id: 'ShieldCheck', label: 'Shield / Certified', icon: ShieldCheck },
  { id: 'Star', label: 'Star / Quality', icon: Star },
  { id: 'PackageCheck', label: 'Package / Delivery', icon: PackageCheck },
  { id: 'ThumbsUp', label: 'Thumbs Up / Satisfaction', icon: ThumbsUp }
];

const CATEGORY_AUDIENCES = ['For Women', 'For Men', 'For Baby', 'Unisex / All'];
const TARGET_AUDIENCE = ['For Women', 'For Men', 'For Baby', 'Unisex'];

const OCCASIONS = [
  'Birthday', 'Wedding Anniversary', 'Wedding', 'Engagement',
  'Naming Ceremony', "Valentine's Day", 'Festival / Special'
];

const RECIPIENTS = ['For Her', 'For Him', 'For Baby'];

const BLOG_CATEGORIES = ['Jewellery Care', 'Trends', 'Buying Guide', 'Occasions', 'Heritage'];

const parseInquiryDate = (createdAt) => {
  if (!createdAt) return null;
  if (typeof createdAt?.toDate === 'function') return createdAt.toDate();
  if (typeof createdAt === 'object' && createdAt.seconds) return new Date(createdAt.seconds * 1000);
  const d = new Date(createdAt);
  return isNaN(d.getTime()) ? null : d;
};

const MONTHS_LIST = [
  { short: 'Jan', full: 'January' },
  { short: 'Feb', full: 'February' },
  { short: 'Mar', full: 'March' },
  { short: 'Apr', full: 'April' },
  { short: 'May', full: 'May' },
  { short: 'Jun', full: 'June' },
  { short: 'Jul', full: 'July' },
  { short: 'Aug', full: 'August' },
  { short: 'Sep', full: 'September' },
  { short: 'Oct', full: 'October' },
  { short: 'Nov', full: 'November' },
  { short: 'Dec', full: 'December' }
];

const parseItemTimestamp = (item) => {
  if (!item) return 0;
  const created = item.createdAt;
  if (created) {
    if (typeof created?.toDate === 'function') return created.toDate().getTime();
    if (typeof created === 'object' && created.seconds) return created.seconds * 1000;
    const d = new Date(created);
    if (!isNaN(d.getTime())) return d.getTime();
  }
  if (item.date) {
    const d = new Date(item.date);
    if (!isNaN(d.getTime())) return d.getTime();
  }
  if (typeof item.id === 'string' && item.id.includes('-')) {
    const parts = item.id.split('-');
    const num = parseInt(parts[1], 10);
    if (!isNaN(num) && num > 1000000000000) return num;
  }
  return 0;
};

// Reusable Elegant Pagination Bar Component
const AdminPagination = ({
  currentPage,
  totalPages,
  totalFilteredCount,
  totalAllCount,
  itemsPerPage,
  itemName = 'items',
  onPageChange
}) => {
  if (totalFilteredCount === 0) return null;

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalFilteredCount);

  return (
    <div className="admin-pagination-container">
      <div className="pagination-info-text">
        Showing <strong>{startItem}</strong> – <strong>{endItem}</strong> of <strong>{totalFilteredCount}</strong> {itemName}
        {totalAllCount !== undefined && totalAllCount !== totalFilteredCount && ` (filtered from ${totalAllCount})`}
      </div>

      <div className="pagination-buttons-wrap">
        <button
          type="button"
          className="page-nav-btn prev"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        >
          <ChevronLeft size={15} />
          <span>Previous</span>
        </button>

        <div className="page-numbers-row">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => {
            if (
              pageNum === 1 ||
              pageNum === totalPages ||
              (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
            ) {
              return (
                <button
                  key={pageNum}
                  type="button"
                  className={`page-num-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => onPageChange(pageNum)}
                >
                  {pageNum}
                </button>
              );
            } else if (
              pageNum === currentPage - 3 ||
              pageNum === currentPage + 3
            ) {
              return <span key={pageNum} className="page-ellipsis">…</span>;
            }
            return null;
          })}
        </div>

        <button
          type="button"
          className="page-nav-btn next"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        >
          <span>Next</span>
          <ChevronRight size={15} />
        </button>
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const {
    products, addProduct, updateProduct, deleteProduct,
    categories, addCategory, updateCategory, deleteCategory,
    gifts, addGift, updateGift, deleteGift,
    blogs, addBlog, updateBlog, deleteBlog,
    inquiries, deleteInquiry, updateInquiry,
    stats, updateStats, resetStats
  } = useContext(AppContext);

  // Handle local file selection and convert to optimized Data URL for Firestore upload & web display
  const handleImageFileUpload = (e, setFormState) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      if (file.size > 15 * 1024 * 1024) {
        showErrorAlert('File Too Large', 'Selected image is larger than 15MB. Please choose a smaller image file.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const maxDim = 1200;
          if (width > maxDim || height > maxDim) {
            if (width > height) {
              height = Math.round((height * maxDim) / width);
              width = maxDim;
            } else {
              width = Math.round((width * maxDim) / height);
              height = maxDim;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.85);
          setFormState(prev => ({ ...prev, image: compressedDataUrl }));
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  // Authentication State
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('gdr_admin_auth') === 'true';
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [isLoadingAuth, setIsLoadingAuth] = useState(false);

  // Tab State: 'dashboard' | 'products' | 'gifts' | 'blogs' | 'inquiries' | 'stats'
  const [activeTab, setActiveTab] = useState('dashboard');

  // Stats Management State
  const [statsForm, setStatsForm] = useState(stats || DEFAULT_STATS);
  const [statsPreviewLang, setStatsPreviewLang] = useState('en');

  useEffect(() => {
    if (stats && stats.length > 0) {
      setStatsForm(stats);
    }
  }, [stats]);

  const handleStatFieldChange = (index, field, value) => {
    setStatsForm(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleSaveStats = async (e) => {
    if (e) e.preventDefault();
    try {
      await updateStats(statsForm);
      showSuccessAlert('Stats Saved Successfully!', 'Homepage live counter statistics have been updated in real-time.');
    } catch (err) {
      showErrorAlert('Save Failed', 'Could not save stats. Please try again.');
    }
  };

  const handleResetStats = async () => {
    const confirmed = await showConfirmDialog(
      'Reset Stats to Defaults?',
      'Are you sure you want to restore the original 4 default statistics (500+ Silver Designs, 15+ Years, etc.)?',
      'Yes, Reset'
    );
    if (confirmed) {
      await resetStats();
      setStatsForm(DEFAULT_STATS);
      showSuccessAlert('Reset Complete', 'Default statistics restored successfully.');
    }
  };

  // Search & Filter States
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedProductAudience, setSelectedProductAudience] = useState('All');

  // Category Management State
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [selectedAudienceFilter, setSelectedAudienceFilter] = useState('All');
  const [isQuickCategoryModalOpen, setIsQuickCategoryModalOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    nameMr: '',
    audience: 'For Women'
  });

  // Modal / Form States
  const [modalType, setModalType] = useState(null); // 'add-product' | 'edit-product' | 'add-category' | 'edit-category' | 'add-gift' | 'edit-gift' | 'add-blog' | 'edit-blog' | 'view-inquiry'
  const [selectedItem, setSelectedItem] = useState(null);

  // Product Form Data
  const [productForm, setProductForm] = useState({
    name: '', nameMr: '', category: '', subCategory: 'For Women',
    price: '', weight: '', rating: '4.8', image: '', description: '', descriptionMr: '', inStock: true
  });

  // Gift Form Data
  const [giftForm, setGiftForm] = useState({
    name: '', nameMr: '', occasion: 'Birthday', forWhom: 'For Her',
    price: '', image: '', description: '', descriptionMr: '', inStock: true
  });

  // Blog Form Data
  const [blogForm, setBlogForm] = useState({
    title: '', titleMr: '', excerpt: '', excerptMr: '', content: '', contentMr: '',
    category: 'Trends', author: 'GDR Editorial', image: '', date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  });

  // Dynamic Real-time Date info for Inquiries Analytics Graph
  const currentRealDate = new Date();
  const currentRealYear = currentRealDate.getFullYear();
  const currentRealMonth = currentRealDate.getMonth();

  // Dynamic Year selection for Inquiries Graph (automatic for next years and past years)
  const availableInquiryYears = useMemo(() => {
    const yearsSet = new Set([currentRealYear]);
    inquiries.forEach(inq => {
      const d = parseInquiryDate(inq.createdAt);
      if (d) yearsSet.add(d.getFullYear());
    });
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [inquiries, currentRealYear]);

  const [selectedChartYear, setSelectedChartYear] = useState(currentRealYear);
  const [hoveredMonth, setHoveredMonth] = useState(null);

  // Calculate monthly stats for the selected year
  const chartMonthlyStats = useMemo(() => {
    const counts = new Array(12).fill(0);
    const messagesByMonth = Array.from({ length: 12 }, () => []);

    inquiries.forEach(inq => {
      const d = parseInquiryDate(inq.createdAt);
      if (d && d.getFullYear() === selectedChartYear) {
        const m = d.getMonth();
        counts[m] += 1;
        messagesByMonth[m].push(inq);
      }
    });

    const totalYearInquiries = counts.reduce((sum, val) => sum + val, 0);
    const maxVal = Math.max(...counts, 4);
    const peakIndex = counts.indexOf(Math.max(...counts));

    return {
      counts,
      messagesByMonth,
      totalYearInquiries,
      maxVal,
      peakMonthName: counts[peakIndex] > 0 ? MONTHS_LIST[peakIndex].full : 'None',
      peakCount: counts[peakIndex]
    };
  }, [inquiries, selectedChartYear]);

  // Dynamic unique category names
  const uniqueCategoryNames = useMemo(() => {
    const set = new Set();
    (categories || []).forEach(c => {
      if (c.name) set.add(c.name.trim());
    });
    return Array.from(set);
  }, [categories]);

  // Filtered categories for Categories Management Tab
  const filteredCategories = useMemo(() => {
    return (categories || []).filter(cat => {
      const q = (categorySearchQuery || '').toLowerCase().trim();
      const matchSearch = !q ||
        (cat.name && cat.name.toLowerCase().includes(q)) ||
        (cat.nameMr && cat.nameMr.toLowerCase().includes(q)) ||
        (cat.audience && cat.audience.toLowerCase().includes(q));

      const matchAudience = selectedAudienceFilter === 'All' ||
        (cat.audience || 'For Women') === selectedAudienceFilter;

      return matchSearch && matchAudience;
    });
  }, [categories, categorySearchQuery, selectedAudienceFilter]);

  // --- PAGINATION & SORTING STATES ---
  // Products Pagination (10 per page)
  const [productPage, setProductPage] = useState(1);
  const PRODUCTS_PER_PAGE = 10;

  // Gifts Pagination (10 per page)
  const [giftPage, setGiftPage] = useState(1);
  const GIFTS_PER_PAGE = 10;

  // Blogs Pagination (10 per page)
  const [blogPage, setBlogPage] = useState(1);
  const BLOGS_PER_PAGE = 10;

  // Inquiries Pagination & Status Filter State (15 items per page)
  const [inquiryPage, setInquiryPage] = useState(1);
  const [inquiryStatusFilter, setInquiryStatusFilter] = useState('All');
  const INQUIRIES_PER_PAGE = 15;

  // Reset pagination on search or filter changes
  useEffect(() => {
    setProductPage(1);
  }, [searchQuery, selectedCategory, selectedProductAudience]);

  useEffect(() => {
    setGiftPage(1);
  }, [searchQuery, selectedCategory]);

  useEffect(() => {
    setBlogPage(1);
  }, [searchQuery]);

  useEffect(() => {
    setInquiryPage(1);
  }, [searchQuery, inquiryStatusFilter]);

  // Helper to determine product audience reliably
  const getProductAudience = useCallback((p) => {
    if (p.subCategory) return p.subCategory;
    if (p.audience) return p.audience;
    if (p.forWhom === 'For Her') return 'For Women';
    if (p.forWhom === 'For Him') return 'For Men';
    if (p.forWhom === 'For Baby') return 'For Baby';
    if (p.category) {
      const matchedCat = (categories || []).find(c => c.name && c.name.toLowerCase().trim() === p.category.toLowerCase().trim());
      if (matchedCat && matchedCat.audience) return matchedCat.audience;
    }
    return 'For Women';
  }, [categories]);

  // Audience product counts for pills
  const productAudienceCounts = useMemo(() => {
    const counts = {
      All: products.length,
      'For Women': 0,
      'For Men': 0,
      'For Baby': 0,
      'Unisex / All': 0
    };
    products.forEach(p => {
      const aud = getProductAudience(p);
      const audClean = (aud || '').toLowerCase().replace(/[^a-z]/g, '');
      if (audClean.includes('women')) counts['For Women'] += 1;
      else if (audClean.includes('men')) counts['For Men'] += 1;
      else if (audClean.includes('baby') || audClean.includes('kid')) counts['For Baby'] += 1;
      else counts['Unisex / All'] += 1;
    });
    return counts;
  }, [products, getProductAudience]);

  // --- PRODUCTS: SORTED (Newest First) & PAGINATED (10 per page) ---
  const sortedProducts = useMemo(() => {
    return [...products].sort((a, b) => {
      const timeA = parseItemTimestamp(a);
      const timeB = parseItemTimestamp(b);
      if (timeA && timeB) return timeB - timeA;
      if (timeA && !timeB) return -1;
      if (!timeA && timeB) return 1;
      return 0;
    });
  }, [products]);

  const filteredProducts = useMemo(() => {
    return sortedProducts.filter(p => {
      const q = (searchQuery || '').toLowerCase().trim();
      const matchesSearch = !q ||
        (p.name && p.name.toLowerCase().includes(q)) ||
        (p.nameMr && p.nameMr.includes(q)) ||
        (p.category && p.category.toLowerCase().includes(q));

      const matchesCat = selectedCategory === 'All' || p.category === selectedCategory;

      let matchesAudience = true;
      if (selectedProductAudience !== 'All') {
        const pAud = getProductAudience(p);
        const pAudClean = (pAud || '').toLowerCase().replace(/[^a-z]/g, '');
        const selAudClean = selectedProductAudience.toLowerCase().replace(/[^a-z]/g, '');
        if (selAudClean.includes('women')) {
          matchesAudience = pAudClean.includes('women');
        } else if (selAudClean.includes('men')) {
          matchesAudience = pAudClean.includes('men') && !pAudClean.includes('women');
        } else if (selAudClean.includes('baby') || selAudClean.includes('kid')) {
          matchesAudience = pAudClean.includes('baby') || pAudClean.includes('kid');
        } else {
          matchesAudience = pAudClean.includes('unisex') || pAudClean.includes('all');
        }
      }

      return matchesSearch && matchesCat && matchesAudience;
    });
  }, [sortedProducts, searchQuery, selectedCategory, selectedProductAudience, getProductAudience]);

  const totalProductPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE) || 1;
  const paginatedProducts = useMemo(() => {
    const startIndex = (productPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);
  }, [filteredProducts, productPage]);

  // --- GIFTS: SORTED (Newest First) & PAGINATED (10 per page) ---
  const sortedGifts = useMemo(() => {
    return [...gifts].sort((a, b) => {
      const timeA = parseItemTimestamp(a);
      const timeB = parseItemTimestamp(b);
      if (timeA && timeB) return timeB - timeA;
      if (timeA && !timeB) return -1;
      if (!timeA && timeB) return 1;
      return 0;
    });
  }, [gifts]);

  const filteredGifts = useMemo(() => {
    return sortedGifts.filter(g => {
      const matchesSearch = !searchQuery ||
        g.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        g.nameMr?.includes(searchQuery) ||
        g.occasion?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        g.forWhom?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesOccasion = selectedCategory === 'All' || g.occasion === selectedCategory;
      return matchesSearch && matchesOccasion;
    });
  }, [sortedGifts, searchQuery, selectedCategory]);

  const totalGiftPages = Math.ceil(filteredGifts.length / GIFTS_PER_PAGE) || 1;
  const paginatedGifts = useMemo(() => {
    const startIndex = (giftPage - 1) * GIFTS_PER_PAGE;
    return filteredGifts.slice(startIndex, startIndex + GIFTS_PER_PAGE);
  }, [filteredGifts, giftPage]);

  // --- BLOGS: SORTED (Newest First) & PAGINATED (10 per page) ---
  const sortedBlogs = useMemo(() => {
    return [...blogs].sort((a, b) => {
      const timeA = parseItemTimestamp(a);
      const timeB = parseItemTimestamp(b);
      if (timeA && timeB) return timeB - timeA;
      if (timeA && !timeB) return -1;
      if (!timeA && timeB) return 1;
      return 0;
    });
  }, [blogs]);

  const filteredBlogs = useMemo(() => {
    return sortedBlogs.filter(b => {
      return !searchQuery ||
        b.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.titleMr?.includes(searchQuery) ||
        b.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        b.author?.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [sortedBlogs, searchQuery]);

  const totalBlogPages = Math.ceil(filteredBlogs.length / BLOGS_PER_PAGE) || 1;
  const paginatedBlogs = useMemo(() => {
    const startIndex = (blogPage - 1) * BLOGS_PER_PAGE;
    return filteredBlogs.slice(startIndex, startIndex + BLOGS_PER_PAGE);
  }, [filteredBlogs, blogPage]);

  // --- INQUIRIES: SORTED (Newest First) & PAGINATED (15 per page) ---
  const sortedInquiries = useMemo(() => {
    return [...inquiries].sort((a, b) => {
      const timeA = parseInquiryDate(a.createdAt)?.getTime() || 0;
      const timeB = parseInquiryDate(b.createdAt)?.getTime() || 0;
      return timeB - timeA;
    });
  }, [inquiries]);

  const filteredInquiries = useMemo(() => {
    return sortedInquiries.filter(i => {
      const matchesSearch = !searchQuery ||
        i.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        i.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        i.phone?.includes(searchQuery) ||
        i.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        i.message?.toLowerCase().includes(searchQuery.toLowerCase());

      const statusVal = i.status || 'New';
      const matchesStatus = inquiryStatusFilter === 'All' || statusVal === inquiryStatusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [sortedInquiries, searchQuery, inquiryStatusFilter]);

  const totalInquiryPages = Math.ceil(filteredInquiries.length / INQUIRIES_PER_PAGE) || 1;
  const paginatedInquiries = useMemo(() => {
    const startIndex = (inquiryPage - 1) * INQUIRIES_PER_PAGE;
    return filteredInquiries.slice(startIndex, startIndex + INQUIRIES_PER_PAGE);
  }, [filteredInquiries, inquiryPage]);

  // Sync Firebase Authentication State
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setIsAuthenticated(true);
        sessionStorage.setItem('gdr_admin_auth', 'true');
      } else {
        setIsAuthenticated(false);
        sessionStorage.removeItem('gdr_admin_auth');
      }
    });
    return () => unsubscribe();
  }, []);

  // Handle Login Submission using Firebase Auth
  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (!email.trim() || !password.trim()) {
      setAuthError('Please enter both Email and Password.');
      return;
    }

    setIsLoadingAuth(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      setIsAuthenticated(true);
      sessionStorage.setItem('gdr_admin_auth', 'true');
    } catch (err) {
      console.error('Firebase Auth Error:', err);
      if (
        err.code === 'auth/invalid-credential' ||
        err.code === 'auth/wrong-password' ||
        err.code === 'auth/user-not-found'
      ) {
        setAuthError('Invalid Email or Password. Please try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setAuthError('Too many failed attempts. Please try again later.');
      } else {
        setAuthError('Authentication failed: ' + (err.message || 'Please check your connection and credentials.'));
      }
    } finally {
      setIsLoadingAuth(false);
    }
  };

  // Handle Logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Logout error:', err);
    }
    setIsAuthenticated(false);
    sessionStorage.removeItem('gdr_admin_auth');
  };

  // Reset Modals
  const closeModal = () => {
    setModalType(null);
    setSelectedItem(null);
  };

  // --- PRODUCT HANDLERS ---
  const handleOpenAddProduct = () => {
    const defaultCat = categories.length > 0 ? categories[0] : null;
    setProductForm({
      name: '',
      nameMr: '',
      category: defaultCat ? defaultCat.name : '',
      subCategory: (defaultCat && defaultCat.audience && defaultCat.audience !== 'Unisex / All') ? defaultCat.audience : 'For Women',
      price: '',
      weight: '',
      rating: '4.8',
      image: '',
      description: '',
      descriptionMr: '',
      inStock: true
    });
    setModalType('add-product');
  };

  const handleOpenEditProduct = (prod) => {
    setSelectedItem(prod);
    setProductForm({
      name: prod.name || '',
      nameMr: prod.nameMr || '',
      category: prod.category || (categories[0]?.name || ''),
      subCategory: prod.subCategory || 'For Women',
      price: prod.price || '',
      weight: prod.weight || '',
      rating: prod.rating !== undefined ? prod.rating : '4.8',
      image: prod.image || '',
      description: prod.description || '',
      descriptionMr: prod.descriptionMr || '',
      inStock: prod.inStock !== false
    });
    setModalType('edit-product');
  };

  const handleProductCategoryChange = (catName) => {
    const matched = categories.find(c => c.name === catName);
    setProductForm(prev => ({
      ...prev,
      category: catName,
      subCategory: (matched && matched.audience && matched.audience !== 'Unisex / All') ? matched.audience : prev.subCategory
    }));
  };

  // --- CATEGORY HANDLERS ---
  const handleOpenAddCategory = () => {
    setCategoryForm({
      name: '',
      nameMr: '',
      audience: selectedAudienceFilter !== 'All' ? selectedAudienceFilter : 'For Women'
    });
    setSelectedItem(null);
    setModalType('add-category');
  };

  const handleOpenEditCategory = (cat) => {
    setSelectedItem(cat);
    setCategoryForm({
      name: cat.name || '',
      nameMr: cat.nameMr || '',
      audience: cat.audience || 'For Women'
    });
    setModalType('edit-category');
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.name.trim()) {
      showErrorAlert('Category Name Required', 'Please enter a category name in English.');
      return;
    }

    try {
      if (modalType === 'add-category') {
        await addCategory(categoryForm);
        showSuccessAlert('Category Added!', `Category "${categoryForm.name}" has been created successfully.`);
      } else if (modalType === 'edit-category' && selectedItem) {
        await updateCategory(selectedItem.id, categoryForm);
        showSuccessAlert('Category Updated!', `Category "${categoryForm.name}" details updated.`);
      }
      closeModal();
    } catch (err) {
      showErrorAlert('Error', 'Failed to save category. Please try again.');
    }
  };

  const handleDeleteCategory = async (cat) => {
    const usingProducts = products.filter(p => p.category === cat.name);
    let confirmMsg = `Are you sure you want to delete category "${cat.name}"?`;
    if (usingProducts.length > 0) {
      confirmMsg = `Warning: ${usingProducts.length} product(s) are currently categorized under "${cat.name}". Deleting it will remove the category from the store filters. Continue?`;
    }

    const confirmed = await showConfirmDialog(
      'Delete Category?',
      confirmMsg,
      'Yes, Delete Category'
    );

    if (confirmed) {
      await deleteCategory(cat.id);
      showSuccessAlert('Deleted!', `Category "${cat.name}" has been deleted.`);
    }
  };

  const handleSaveQuickCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.name.trim()) {
      showErrorAlert('Category Name Required', 'Please enter a category name.');
      return;
    }
    const catName = categoryForm.name.trim();
    await addCategory(categoryForm);
    setProductForm(prev => ({
      ...prev,
      category: catName,
      subCategory: (categoryForm.audience && categoryForm.audience !== 'Unisex / All') ? categoryForm.audience : prev.subCategory
    }));
    showSuccessAlert('Category Created!', `Category "${catName}" added and selected.`);
    setIsQuickCategoryModalOpen(false);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    const formattedData = {
      ...productForm,
      price: Number(productForm.price) || 0,
      weight: Number(productForm.weight) || 0,
      rating: Number(productForm.rating) || 4.8
    };

    if (modalType === 'add-product') {
      await addProduct(formattedData);
      showSuccessAlert('Product Added Successfully!', 'The new product is now live in your catalog.');
    } else if (modalType === 'edit-product' && selectedItem) {
      await updateProduct(selectedItem.id, formattedData);
      showSuccessAlert('Product Saved Successfully!', 'The product details have been updated.');
    }
    closeModal();
  };

  const handleDeleteProduct = async (id) => {
    const confirmed = await showConfirmDialog('Delete Product?', 'Are you sure you want to delete this product? This action cannot be undone.', 'Yes, Delete');
    if (confirmed) {
      await deleteProduct(id);
      showSuccessAlert('Deleted!', 'The product has been removed.');
    }
  };

  // --- GIFT HANDLERS ---
  const handleOpenAddGift = () => {
    setGiftForm({
      name: '', nameMr: '', occasion: 'Birthday', forWhom: 'For Her',
      price: '', image: '', description: '', descriptionMr: '', inStock: true
    });
    setModalType('add-gift');
  };

  const handleOpenEditGift = (gift) => {
    setSelectedItem(gift);
    setGiftForm({
      name: gift.name || '',
      nameMr: gift.nameMr || '',
      occasion: gift.occasion || 'Birthday',
      forWhom: gift.forWhom || 'For Her',
      price: gift.price || '',
      image: gift.image || '',
      description: gift.description || '',
      descriptionMr: gift.descriptionMr || '',
      inStock: gift.inStock !== false
    });
    setModalType('edit-gift');
  };

  const handleSaveGift = async (e) => {
    e.preventDefault();
    const formattedData = {
      ...giftForm,
      price: Number(giftForm.price) || 0
    };

    if (modalType === 'add-gift') {
      await addGift(formattedData);
      showSuccessAlert('Gift Item Added Successfully!', 'New gift product has been added.');
    } else if (modalType === 'edit-gift' && selectedItem) {
      await updateGift(selectedItem.id, formattedData);
      showSuccessAlert('Gift Item Saved Successfully!', 'The gift details have been updated.');
    }
    closeModal();
  };

  const handleDeleteGift = async (id) => {
    const confirmed = await showConfirmDialog('Delete Gift Item?', 'Are you sure you want to delete this gift item?', 'Yes, Delete');
    if (confirmed) {
      await deleteGift(id);
      showSuccessAlert('Deleted!', 'The gift item has been removed.');
    }
  };

  // --- BLOG HANDLERS ---
  const handleOpenAddBlog = () => {
    setBlogForm({
      title: '', titleMr: '', excerpt: '', excerptMr: '', content: '', contentMr: '',
      category: 'Trends', author: 'GDR Editorial', image: '', date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    });
    setModalType('add-blog');
  };

  const handleOpenEditBlog = (blog) => {
    setSelectedItem(blog);
    setBlogForm({
      title: blog.title || '',
      titleMr: blog.titleMr || '',
      excerpt: blog.excerpt || '',
      excerptMr: blog.excerptMr || '',
      content: blog.content || '',
      contentMr: blog.contentMr || '',
      category: blog.category || 'Trends',
      author: blog.author || 'GDR Editorial',
      image: blog.image || '',
      date: blog.date || new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    });
    setModalType('edit-blog');
  };

  const handleSaveBlog = async (e) => {
    e.preventDefault();
    if (modalType === 'add-blog') {
      await addBlog(blogForm);
      showSuccessAlert('Blog Post Added Successfully!', 'New blog article has been published.');
    } else if (modalType === 'edit-blog' && selectedItem) {
      await updateBlog(selectedItem.id, blogForm);
      showSuccessAlert('Blog Post Saved Successfully!', 'The blog article has been updated.');
    }
    closeModal();
  };

  const handleDeleteBlog = async (id) => {
    const confirmed = await showConfirmDialog('Delete Blog Post?', 'Are you sure you want to delete this blog post?', 'Yes, Delete');
    if (confirmed) {
      await deleteBlog(id);
      showSuccessAlert('Deleted!', 'The blog post has been removed.');
    }
  };

  // --- INQUIRY HANDLERS ---
  const handleDeleteInquiryItem = async (id) => {
    const confirmed = await showConfirmDialog('Delete Message?', 'Are you sure you want to delete this contact inquiry?', 'Yes, Delete');
    if (confirmed) {
      await deleteInquiry(id);
      showSuccessAlert('Deleted!', 'The contact message has been removed.');
    }
  };

  const handleUpdateInquiryStatus = async (id, status) => {
    if (updateInquiry) {
      await updateInquiry(id, { status });
    }
  };

  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);

  // 1. RENDER LOGIN SCREEN IF NOT AUTHENTICATED
  if (!isAuthenticated) {
    return (
      <div className="admin-login-wrapper">
        <div className="login-glow-orb glow-1"></div>
        <div className="login-glow-orb glow-2"></div>

        <div className="admin-login-card">
          <div className="admin-login-badge-wrap">
            <span className="admin-secure-badge">
              <ShieldCheck size={14} /> RESTRICTED ACCESS
            </span>
          </div>

          <div className="admin-login-header">
            <img src="/images/logo.png" alt="GDR Jewellers" className="admin-login-logo" />
            <h2>GDR Admin Portal</h2>
            <p>Welcome back! Enter your credentials to manage products, gifting, blog & inquiries.</p>
          </div>

          {authError && (
            <div className="admin-auth-error">
              <span>{authError}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="admin-login-form">
            <div className="form-group">
              <label>Administrator Email</label>
              <div className="input-with-icon">
                <Mail size={18} className="field-icon" />
                <input
                  type="email"
                  placeholder="gdrsilver@gmail.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Password</label>
              <div className="input-with-icon">
                <Lock size={18} className="field-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="pwd-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? "Hide Password" : "Show Password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button type="submit" className="admin-login-btn" disabled={isLoadingAuth}>
              {isLoadingAuth ? <RefreshCw size={18} className="spin-icon" /> : <LogIn size={18} />}
              <span>{isLoadingAuth ? 'Authenticating...' : 'Sign In with Firebase'}</span>
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Computed Dashboard Metrics
  const totalProductsCount = products.length;
  const inStockProductsCount = products.filter(p => p.inStock !== false).length;
  const totalCatalogueValue = products.reduce((acc, p) => acc + (Number(p.price) || 0), 0);

  const totalGiftsCount = gifts.length;
  const totalBlogsCount = blogs.length;
  const totalInquiriesCount = inquiries.length;

  // Category counts breakdown
  const categoryStats = uniqueCategoryNames.map(cat => ({
    name: cat,
    count: products.filter(p => p.category === cat).length
  })).sort((a, b) => b.count - a.count);



  // 2. RENDER ADMIN DASHBOARD
  return (
    <div className="admin-dashboard-container">
      {/* Top Navbar */}
      <header className="admin-topbar">
        <div className="admin-topbar-left">
          <img src="/images/logo.png" alt="GDR Logo" className="admin-nav-logo" />
        </div>

        <div className="admin-topbar-right">
          <button className="admin-logout-btn" onClick={handleLogout} title="Sign Out">
            <LogOut size={16} />
            <span>Logout</span>
          </button>
        </div>
      </header>

      {/* Main Content Area with Navigation Sidebar / Tabs */}
      <div className="admin-main-body">
        {/* Navigation Sidebar */}
        <aside className="admin-sidebar">
          <nav className="admin-nav-menu">
            <button
              className={`admin-nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => { setActiveTab('dashboard'); setSearchQuery(''); setSelectedCategory('All'); }}
            >
              <LayoutDashboard size={18} />
              <span>Overview Dashboard</span>
            </button>

            <button
              className={`admin-nav-tab ${activeTab === 'products' ? 'active' : ''}`}
              onClick={() => { setActiveTab('products'); setSearchQuery(''); setSelectedCategory('All'); setSelectedProductAudience('All'); }}
            >
              <ShoppingBag size={18} />
              <span>Products</span>
              <span className="tab-count">{products.length}</span>
            </button>

            <button
              className={`admin-nav-tab ${activeTab === 'categories' ? 'active' : ''}`}
              onClick={() => { setActiveTab('categories'); setCategorySearchQuery(''); setSelectedAudienceFilter('All'); }}
            >
              <Layers size={18} />
              <span>Product Categories</span>
              <span className="tab-count">{categories.length}</span>
            </button>

            <button
              className={`admin-nav-tab ${activeTab === 'gifts' ? 'active' : ''}`}
              onClick={() => { setActiveTab('gifts'); setSearchQuery(''); setSelectedCategory('All'); }}
            >
              <Gift size={18} />
              <span>Gifts by Occasion</span>
              <span className="tab-count">{gifts.length}</span>
            </button>

            <button
              className={`admin-nav-tab ${activeTab === 'blogs' ? 'active' : ''}`}
              onClick={() => { setActiveTab('blogs'); setSearchQuery(''); setSelectedCategory('All'); }}
            >
              <FileText size={18} />
              <span>Blog Posts</span>
              <span className="tab-count">{blogs.length}</span>
            </button>

            <button
              className={`admin-nav-tab ${activeTab === 'inquiries' ? 'active' : ''}`}
              onClick={() => { setActiveTab('inquiries'); setSearchQuery(''); setSelectedCategory('All'); }}
            >
              <MessageSquare size={18} />
              <span>Contact Inquiries</span>
              <span className="tab-count">{inquiries.length}</span>
            </button>

            <button
              className={`admin-nav-tab ${activeTab === 'stats' ? 'active' : ''}`}
              onClick={() => { setActiveTab('stats'); setSearchQuery(''); setSelectedCategory('All'); }}
            >
              <TrendingUp size={18} />
              <span>Homepage Stats</span>
            </button>
          </nav>
        </aside>

        {/* Dashboard Section Content */}
        <main className="admin-content-area">
          {/* Action Header */}
          <div className="admin-section-header">
            <div className="admin-section-title-wrap">
              <h2>
                {activeTab === 'dashboard' && 'Website Overview & Analytics'}
                {activeTab === 'products' && 'Manage Products'}
                {activeTab === 'categories' && 'Manage Product Categories'}
                {activeTab === 'gifts' && 'Manage Gifts by Occasion'}
                {activeTab === 'blogs' && 'Manage Blog Posts'}
                {activeTab === 'inquiries' && 'Customer Inquiries & Messages'}
                {activeTab === 'stats' && 'Manage Homepage Statistics'}
              </h2>
            </div>

            {activeTab !== 'dashboard' && activeTab !== 'stats' && (
              <div className="admin-action-tools">
                <div className="admin-search-box">
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder={`Search ${activeTab}...`}
                    value={activeTab === 'categories' ? categorySearchQuery : searchQuery}
                    onChange={(e) => activeTab === 'categories' ? setCategorySearchQuery(e.target.value) : setSearchQuery(e.target.value)}
                  />
                  {(activeTab === 'categories' ? categorySearchQuery : searchQuery) && (
                    <button onClick={() => activeTab === 'categories' ? setCategorySearchQuery('') : setSearchQuery('')} className="clear-search">
                      <X size={14} />
                    </button>
                  )}
                </div>

                {activeTab === 'products' && (
                  <button className="admin-add-btn" onClick={handleOpenAddProduct}>
                    <Plus size={16} />
                    <span>Add New Product</span>
                  </button>
                )}

                {activeTab === 'categories' && (
                  <button className="admin-add-btn" onClick={handleOpenAddCategory}>
                    <Plus size={16} />
                    <span>Add New Category</span>
                  </button>
                )}

                {activeTab === 'gifts' && (
                  <button className="admin-add-btn" onClick={handleOpenAddGift}>
                    <Plus size={16} />
                    <span>Add Gift Item</span>
                  </button>
                )}

                {activeTab === 'blogs' && (
                  <button className="admin-add-btn" onClick={handleOpenAddBlog}>
                    <Plus size={16} />
                    <span>New Blog Post</span>
                  </button>
                )}
              </div>
            )}

            {activeTab === 'stats' && (
              <div className="admin-action-tools">
                <button type="button" className="admin-btn-secondary" onClick={handleResetStats}>
                  <RotateCcw size={16} />
                  <span>Reset All to Defaults</span>
                </button>
                <button type="button" className="admin-add-btn admin-btn-primary-large" onClick={handleSaveStats}>
                  <Save size={16} />
                  <span>Save & Update Homepage Stats</span>
                </button>
              </div>
            )}
          </div>

          {/* TAB 0: OVERVIEW DASHBOARD TAB */}
          {activeTab === 'dashboard' && (
            <div className="admin-tab-pane dashboard-overview-pane">
              {/* KPI Stat Cards Grid */}
              <div className="admin-stats-grid">
                <div className="stat-card" onClick={() => setActiveTab('products')}>
                  <div className="stat-icon-wrap gold">
                    <ShoppingBag size={24} />
                  </div>
                  <div className="stat-details">
                    <span className="stat-title">Total Products</span>
                    <h3 className="stat-value">{totalProductsCount}</h3>
                    <span className="stat-sub">{inStockProductsCount} In Stock · ₹{totalCatalogueValue.toLocaleString('en-IN')} Value</span>
                  </div>
                  <ArrowRight size={18} className="stat-arrow" />
                </div>

                <div className="stat-card" onClick={() => setActiveTab('gifts')}>
                  <div className="stat-icon-wrap bronze">
                    <Gift size={24} />
                  </div>
                  <div className="stat-details">
                    <span className="stat-title">Gifts by Occasion</span>
                    <h3 className="stat-value">{totalGiftsCount}</h3>
                    <span className="stat-sub">Across {OCCASIONS.length} Occasions & {RECIPIENTS.length} Recipients</span>
                  </div>
                  <ArrowRight size={18} className="stat-arrow" />
                </div>

                <div className="stat-card" onClick={() => setActiveTab('blogs')}>
                  <div className="stat-icon-wrap cream">
                    <FileText size={24} />
                  </div>
                  <div className="stat-details">
                    <span className="stat-title">Blog Articles</span>
                    <h3 className="stat-value">{totalBlogsCount}</h3>
                    <span className="stat-sub">Published across {BLOG_CATEGORIES.length} Topics</span>
                  </div>
                  <ArrowRight size={18} className="stat-arrow" />
                </div>

                <div className="stat-card" onClick={() => setActiveTab('inquiries')}>
                  <div className="stat-icon-wrap danger">
                    <MessageSquare size={24} />
                  </div>
                  <div className="stat-details">
                    <span className="stat-title">Contact Inquiries</span>
                    <h3 className="stat-value">{totalInquiriesCount}</h3>
                    <span className="stat-sub">Direct Customer Messages</span>
                  </div>
                  <ArrowRight size={18} className="stat-arrow" />
                </div>
              </div>



              {/* Quick Shortcuts Bar */}
              <div className="dash-quick-bar">
                <span className="quick-bar-title"><Sparkles size={16} /> Quick Actions:</span>
                <button className="quick-action-btn primary" onClick={handleOpenAddProduct}>
                  <Plus size={15} /> <span>Add New Product</span>
                </button>
                <button className="quick-action-btn primary" onClick={handleOpenAddGift}>
                  <Plus size={15} /> <span>Add Gift Item</span>
                </button>
                <button className="quick-action-btn primary" onClick={handleOpenAddBlog}>
                  <Plus size={15} /> <span>Write Blog Article</span>
                </button>
                <button className="quick-action-btn secondary" onClick={() => setActiveTab('inquiries')}>
                  <MessageSquare size={15} /> <span>Review Customer Messages ({totalInquiriesCount})</span>
                </button>
                <a href="/" target="_blank" rel="noopener noreferrer" className="quick-action-btn outline">
                  <ExternalLink size={15} /> <span>Preview Storefront</span>
                </a>
              </div>

              {/* Dynamic Month-wise Inquiries Analytics Graph */}
              <div className="dash-panel-card dash-full-width dash-chart-card">
                <div className="dash-card-header">
                  <div className="dash-chart-title-group">
                    <h3>
                      <BarChart2 size={20} className="dash-chart-icon" />
                      <span>Contact Inquiries Analytics</span>
                    </h3>
                    <span className="dash-header-subtitle">
                      Dynamic month-by-month customer inquiries received from Contact Us page
                    </span>
                  </div>

                  <div className="dash-chart-controls">
                    <div className="chart-year-selector-wrap">
                      <Calendar size={15} className="chart-cal-icon" />
                      <select
                        id="chart-year-select"
                        value={selectedChartYear}
                        onChange={(e) => setSelectedChartYear(Number(e.target.value))}
                        className="chart-year-dropdown"
                        aria-label="Select Year"
                      >
                        {availableInquiryYears.map(yr => (
                          <option key={yr} value={yr}>Year {yr}</option>
                        ))}
                      </select>
                    </div>

                    <span className="chart-total-pill">
                      <TrendingUp size={14} />
                      <span>{chartMonthlyStats.totalYearInquiries} Total ({selectedChartYear})</span>
                    </span>
                  </div>
                </div>

                <div className="dash-card-body">
                  {/* Chart Mini Summary Metrics */}
                  <div className="chart-mini-summary">
                    <div className="mini-stat-box">
                      <span className="mini-stat-label">Total Inquiries ({selectedChartYear})</span>
                      <strong className="mini-stat-val">{chartMonthlyStats.totalYearInquiries}</strong>
                    </div>
                    <div className="mini-stat-box">
                      <span className="mini-stat-label">Peak Month</span>
                      <strong className="mini-stat-val">
                        {chartMonthlyStats.peakMonthName} {chartMonthlyStats.peakCount > 0 ? `(${chartMonthlyStats.peakCount})` : ''}
                      </strong>
                    </div>
                    <div className="mini-stat-box">
                      <span className="mini-stat-label">Monthly Average</span>
                      <strong className="mini-stat-val">
                        {(chartMonthlyStats.totalYearInquiries / 12).toFixed(1)} / mo
                      </strong>
                    </div>
                    <div className="mini-stat-box highlight">
                      <span className="mini-stat-label">
                        {selectedChartYear === currentRealYear ? `Current Month (${MONTHS_LIST[currentRealMonth].short})` : 'Data Status'}
                      </span>
                      <strong className="mini-stat-val">
                        {selectedChartYear === currentRealYear ? `${chartMonthlyStats.counts[currentRealMonth]} Received` : 'Archive'}
                      </strong>
                    </div>
                  </div>

                  {/* Visual Bar Chart */}
                  <div className="inquiry-chart-container">
                    <div className="chart-y-axis">
                      <span>{chartMonthlyStats.maxVal}</span>
                      <span>{Math.round(chartMonthlyStats.maxVal * 0.75)}</span>
                      <span>{Math.round(chartMonthlyStats.maxVal * 0.5)}</span>
                      <span>{Math.round(chartMonthlyStats.maxVal * 0.25)}</span>
                      <span>0</span>
                    </div>

                    <div className="chart-bars-track">
                      {/* Horizontal Grid Guidelines */}
                      <div className="chart-grid-lines">
                        <div className="grid-line" style={{ bottom: '100%' }}></div>
                        <div className="grid-line" style={{ bottom: '75%' }}></div>
                        <div className="grid-line" style={{ bottom: '50%' }}></div>
                        <div className="grid-line" style={{ bottom: '25%' }}></div>
                        <div className="grid-line" style={{ bottom: '0%' }}></div>
                      </div>

                      {/* 12 Monthly Bars */}
                      <div className="chart-bars-row">
                        {MONTHS_LIST.map((m, idx) => {
                          const count = chartMonthlyStats.counts[idx];
                          const heightPct = Math.min(100, Math.max(count > 0 ? 8 : 2, (count / chartMonthlyStats.maxVal) * 100));
                          const isCurrentMonth = selectedChartYear === currentRealYear && idx === currentRealMonth;
                          const isHovered = hoveredMonth === idx;

                          return (
                            <div
                              key={m.short}
                              className={`chart-bar-col ${isCurrentMonth ? 'current-month' : ''} ${isHovered ? 'hovered' : ''}`}
                              onMouseEnter={() => setHoveredMonth(idx)}
                              onMouseLeave={() => setHoveredMonth(null)}
                              onClick={() => {
                                if (count > 0) setActiveTab('inquiries');
                              }}
                            >
                              {/* Hover Floating Tooltip */}
                              {isHovered && (
                                <div className="chart-tooltip">
                                  <strong className="tooltip-title">{m.full} {selectedChartYear}</strong>
                                  <span className="tooltip-count">{count} {count === 1 ? 'Inquiry' : 'Inquiries'}</span>
                                  {count > 0 ? (
                                    <span className="tooltip-hint">Click to view in Inquiries tab →</span>
                                  ) : (
                                    <span className="tooltip-hint">No messages in this month</span>
                                  )}
                                </div>
                              )}

                              {/* Value Label above bar */}
                              <div className={`bar-value-label ${count > 0 ? 'active' : ''}`}>
                                {count}
                              </div>

                              {/* Bar Pillar */}
                              <div className="bar-pillar-wrap">
                                <div
                                  className={`bar-pillar ${count > 0 ? 'filled' : 'empty'}`}
                                  style={{ height: `${heightPct}%` }}
                                >
                                  {count > 0 && <div className="bar-glow-cap"></div>}
                                </div>
                              </div>

                              {/* Month X-Axis Label */}
                              <div className="bar-month-label">
                                <span>{m.short}</span>
                                {isCurrentMonth && <span className="current-dot" title="Current Month"></span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Chart Footer Legend & Real-time status */}
                  <div className="chart-footer-legend">
                    <div className="legend-items-wrap">
                      <div className="legend-item">
                        <span className="legend-swatch active"></span>
                        <span>Monthly Inquiries Volume</span>
                      </div>
                      {selectedChartYear === currentRealYear && (
                        <div className="legend-item">
                          <span className="legend-swatch current"></span>
                          <span>Current Month ({MONTHS_LIST[currentRealMonth].full})</span>
                        </div>
                      )}
                    </div>
                    <div className="legend-sync-tag">
                      <CheckCircle2 size={13} />
                      <span>Live synced with Contact Us inquiries</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Category Inventory Breakdown Grid */}
              <div className="dash-panel-card dash-full-width dash-categories-card">
                <div className="dash-card-header">
                  <div className="dash-header-title-group">
                    <h3><Layers size={18} /> Category Inventory Breakdown</h3>
                    <span className="dash-header-subtitle">Distribution of {totalProductsCount} jewellery products across store categories</span>
                  </div>
                  <span className="cat-total-badge">{uniqueCategoryNames.length} Categories</span>
                </div>
                <div className="dash-card-body">
                  <div className="dash-category-grid">
                    {categoryStats.map(cat => {
                      const pct = totalProductsCount > 0 ? Math.round((cat.count / totalProductsCount) * 100) : 0;
                      return (
                        <div key={cat.name} className="dash-cat-card" onClick={() => { setActiveTab('products'); setSelectedCategory(cat.name); }}>
                          <div className="cat-card-header">
                            <span className="cat-name" title={cat.name}>{cat.name}</span>
                            <span className={`cat-count-badge ${cat.count > 0 ? 'has-items' : 'empty'}`}>
                              {cat.count} {cat.count === 1 ? 'item' : 'items'}
                            </span>
                          </div>
                          <div className="cat-progress-wrap">
                            <div className="cat-progress-bar">
                              <div
                                className="cat-progress-fill"
                                style={{ width: `${totalProductsCount > 0 ? Math.max(cat.count > 0 ? 6 : 0, (cat.count / totalProductsCount) * 100) : 0}%` }}
                              ></div>
                            </div>
                            <span className="cat-pct-label">{pct}%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Grid of Activity Panels (2x2 Grid) */}
              <div className="dash-columns-grid">
                {/* Panel 1: Recent Customer Messages */}
                <div className="dash-panel-card">
                  <div className="dash-card-header">
                    <h3><MessageSquare size={18} /> Recent Customer Inquiries</h3>
                    <button className="view-all-link" onClick={() => setActiveTab('inquiries')}>
                      View All ({totalInquiriesCount}) <ArrowRight size={14} />
                    </button>
                  </div>

                  <div className="dash-card-body">
                    {inquiries.length === 0 ? (
                      <div className="dash-empty-state">No customer messages received yet.</div>
                    ) : (
                      <div className="dash-inquiries-list">
                        {inquiries.slice(0, 4).map(inq => (
                          <div key={inq.id} className="dash-inquiry-item" onClick={() => setActiveTab('inquiries')}>
                            <div className="inq-avatar">{inq.name ? inq.name[0].toUpperCase() : 'U'}</div>
                            <div className="inq-info">
                              <strong className="inq-sender">{inq.name}</strong>
                              <span className="inq-subject">{inq.subject || 'General Inquiry'}</span>
                              <p className="inq-snippet">{inq.message}</p>
                            </div>
                            <span className="inq-time">
                              {inq.createdAt ? new Date(inq.createdAt).toLocaleDateString() : 'Recent'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Panel 2: Recent Products Preview */}
                <div className="dash-panel-card">
                  <div className="dash-card-header">
                    <h3><ShoppingBag size={18} /> Recent Products</h3>
                    <button className="view-all-link" onClick={() => setActiveTab('products')}>
                      Manage Products ({totalProductsCount}) <ArrowRight size={14} />
                    </button>
                  </div>

                  <div className="dash-card-body">
                    {products.length === 0 ? (
                      <div className="dash-empty-state">No products in store.</div>
                    ) : (
                      <div className="dash-products-list">
                        {products.slice(0, 5).map(p => (
                          <div key={p.id} className="dash-product-item">
                            <img src={p.image || '/images/placeholder.jpg'} alt={p.name} className="dash-prod-thumb" />
                            <div className="dash-prod-info">
                              <strong className="dash-prod-name">{p.name}</strong>
                              <span className="dash-prod-cat">{p.category}</span>
                            </div>
                            <span className="dash-prod-price">₹{Number(p.price).toLocaleString()}</span>
                            <button className="dash-edit-icon" onClick={() => handleOpenEditProduct(p)} title="Edit Product">
                              <Edit3 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Panel 3: Gifts by Occasion Highlights */}
                <div className="dash-panel-card">
                  <div className="dash-card-header">
                    <h3><Gift size={18} /> Gifts by Occasion Highlights</h3>
                    <button className="view-all-link" onClick={() => setActiveTab('gifts')}>
                      Manage Gifts ({totalGiftsCount}) <ArrowRight size={14} />
                    </button>
                  </div>

                  <div className="dash-card-body">
                    {gifts.length === 0 ? (
                      <div className="dash-empty-state">No gifts added yet.</div>
                    ) : (
                      <div className="dash-products-list">
                        {gifts.slice(0, 4).map(g => (
                          <div key={g.id} className="dash-product-item">
                            <img src={g.image || '/images/placeholder.jpg'} alt={g.name} className="dash-prod-thumb" />
                            <div className="dash-prod-info">
                              <strong className="dash-prod-name">{g.name}</strong>
                              <span className="dash-prod-cat">{g.occasion} · {g.forWhom}</span>
                            </div>
                            <span className="dash-prod-price">₹{Number(g.price).toLocaleString()}</span>
                            <button className="dash-edit-icon" onClick={() => handleOpenEditGift(g)} title="Edit Gift">
                              <Edit3 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Panel 4: Published Blog Posts Highlights */}
                <div className="dash-panel-card">
                  <div className="dash-card-header">
                    <h3><FileText size={18} /> Published Blog Articles</h3>
                    <button className="view-all-link" onClick={() => setActiveTab('blogs')}>
                      Manage Articles ({totalBlogsCount}) <ArrowRight size={14} />
                    </button>
                  </div>

                  <div className="dash-card-body">
                    {blogs.length === 0 ? (
                      <div className="dash-empty-state">No blog posts published yet.</div>
                    ) : (
                      <div className="dash-products-list">
                        {blogs.slice(0, 4).map(b => (
                          <div key={b.id} className="dash-product-item">
                            <img src={b.image || '/images/placeholder.jpg'} alt={b.title} className="dash-prod-thumb blog-thumb" />
                            <div className="dash-prod-info">
                              <strong className="dash-prod-name">{b.title}</strong>
                              <span className="dash-prod-cat">{b.category} · {b.date}</span>
                            </div>
                            <button className="dash-edit-icon" onClick={() => handleOpenEditBlog(b)} title="Edit Blog">
                              <Edit3 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 1: PRODUCTS TAB */}
          {activeTab === 'products' && (
            <div className="admin-tab-pane">
              <div className="admin-filter-bar products-filter-bar">
                {/* Target Audience Filter Pills */}
                <div className="cat-audience-tabs">
                  {[
                    { label: 'All', value: 'All' },
                    { label: 'For Women', value: 'For Women' },
                    { label: 'For Men', value: 'For Men' },
                    { label: 'For Baby', value: 'For Baby' },
                    { label: 'Unisex / All', value: 'Unisex / All' }
                  ].map(aud => (
                    <button
                      key={aud.value}
                      type="button"
                      className={`cat-aud-pill ${selectedProductAudience === aud.value ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedProductAudience(aud.value);
                        setProductPage(1);
                      }}
                    >
                      <span>{aud.label}</span>
                      <span className="pill-count">{productAudienceCounts[aud.value] || 0}</span>
                    </button>
                  ))}
                </div>

                {/* Category Dropdown Filter */}
                <div className="admin-filter-dropdown-wrap">
                  <Filter size={16} className="filter-dropdown-icon" />
                  <label htmlFor="product-cat-select" className="filter-label">Filter Category:</label>
                  <select
                    id="product-cat-select"
                    className="admin-filter-select"
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setProductPage(1);
                    }}
                  >
                    <option value="All">All Categories ({products.length})</option>
                    {uniqueCategoryNames.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="admin-table-container">
                <table className="admin-data-table">
                  <thead>
                    <tr>
                      <th style={{ width: '60px' }}>Image</th>
                      <th>Product Name</th>
                      <th>Marathi Name</th>
                      <th>Category</th>
                      <th style={{ textAlign: 'center' }}>Target Audience</th>
                      <th>Price (₹)</th>
                      <th>Weight</th>
                      <th>Status</th>
                      <th style={{ textAlign: 'center', width: '120px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="admin-table-empty">
                          <div className="empty-state-box">
                            <div className="empty-icon-wrap">
                              <ShoppingBag size={32} className="empty-icon" />
                            </div>
                            <h4>No Products Found</h4>
                            <button
                              type="button"
                              className="admin-add-btn empty-state-add-btn"
                              onClick={handleOpenAddProduct}
                            >
                              <Plus size={16} /> Add Product
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedProducts.map(p => {
                        const pAud = getProductAudience(p);
                        const audClass = (pAud || 'For Women').toLowerCase().replace(/[^a-z]/g, '');
                        return (
                          <tr key={p.id}>
                            <td>
                              <img src={p.image || '/images/placeholder.jpg'} alt={p.name} className="table-thumb" />
                            </td>
                            <td>
                              <strong className="item-title">{p.name}</strong>
                            </td>
                            <td>{p.nameMr || '-'}</td>
                            <td><span className="cat-badge">{p.category}</span></td>
                            <td style={{ textAlign: 'center' }}>
                              <span className={`cat-aud-badge aud-${audClass}`}>
                                {pAud}
                              </span>
                            </td>
                            <td className="admin-price-cell"><strong className="admin-price-val" style={{ fontFamily: "'Poppins', sans-serif" }}>₹{Number(p.price).toLocaleString()}</strong></td>
                            <td>{p.weight ? `${p.weight} g` : '-'}</td>
                            <td>
                              <span className={`status-pill ${p.inStock !== false ? 'in-stock' : 'out-of-stock'}`}>
                                {p.inStock !== false ? 'In Stock' : 'Out of Stock'}
                              </span>
                            </td>
                            <td>
                              <div className="table-actions" style={{ justifyContent: 'center' }}>
                                <button className="action-btn edit" onClick={() => handleOpenEditProduct(p)} title="Edit Product">
                                  <Edit3 size={15} />
                                </button>
                                <button className="action-btn delete" onClick={() => handleDeleteProduct(p.id)} title="Delete Product">
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Products Pagination Bar (10 per page) */}
              <AdminPagination
                currentPage={productPage}
                totalPages={totalProductPages}
                totalFilteredCount={filteredProducts.length}
                totalAllCount={products.length}
                itemsPerPage={PRODUCTS_PER_PAGE}
                itemName="products"
                onPageChange={setProductPage}
              />
            </div>
          )}

          {/* TAB: PRODUCT CATEGORIES TAB */}
          {activeTab === 'categories' && (
            <div className="admin-tab-pane categories-management-pane">
              {/* Audience Filter Bar & Summary */}
              <div className="admin-filter-bar categories-filter-bar">
                <div className="cat-audience-tabs">
                  {['All', 'For Women', 'For Men', 'For Baby', 'Unisex / All'].map(aud => (
                    <button
                      key={aud}
                      type="button"
                      className={`cat-aud-pill ${selectedAudienceFilter === aud ? 'active' : ''}`}
                      onClick={() => setSelectedAudienceFilter(aud)}
                    >
                      <span>{aud}</span>
                      <span className="pill-count">
                        {aud === 'All'
                          ? categories.length
                          : categories.filter(c => (c.audience || 'For Women') === aud).length}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Categories Data Table */}
              <div className="admin-table-container">
                <table className="admin-data-table categories-table">
                  <thead>
                    <tr>
                      <th className="th-idx">#</th>
                      <th>Category Name (English)</th>
                      <th>Category Name (मराठी नाव)</th>
                      <th className="th-center">Target Audience / Group</th>
                      <th className="th-center">Total Products</th>
                      <th className="th-center th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCategories.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="admin-table-empty">
                          <div className="empty-state-box">
                            <div className="empty-icon-wrap">
                              <Layers size={32} className="empty-icon" />
                            </div>
                            <h4>No Categories Found</h4>
                            <button
                              type="button"
                              className="admin-add-btn empty-state-add-btn"
                              onClick={handleOpenAddCategory}
                            >
                              <Plus size={16} /> Add Category
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      filteredCategories.map((cat, idx) => {
                        const productCount = products.filter(p => p.category === cat.name).length;
                        const audClass = (cat.audience || 'For Women').toLowerCase().replace(/[^a-z]/g, '');
                        return (
                          <tr key={cat.id || idx}>
                            <td className="cat-col-idx">{idx + 1}</td>
                            <td className="cat-col-name">
                              <strong>{cat.name}</strong>
                            </td>
                            <td className="cat-col-mr">
                              <span>{cat.nameMr || '—'}</span>
                            </td>
                            <td className="cat-col-center">
                              <span className={`cat-aud-badge aud-${audClass}`}>
                                {cat.audience || 'For Women'}
                              </span>
                            </td>
                            <td className="cat-col-center">
                              <button
                                type="button"
                                className={`cat-products-count-badge ${productCount > 0 ? 'has-products' : 'zero'}`}
                                onClick={() => {
                                  setActiveTab('products');
                                  setSelectedCategory(cat.name);
                                }}
                                title="Click to view products in this category"
                              >
                                {productCount} {productCount === 1 ? 'Product' : 'Products'}
                              </button>
                            </td>
                            <td className="admin-actions-cell">
                              <div className="table-actions">
                                <button
                                  type="button"
                                  className="action-btn edit"
                                  title="Edit Category"
                                  onClick={() => handleOpenEditCategory(cat)}
                                >
                                  <Edit3 size={15} />
                                </button>
                                <button
                                  type="button"
                                  className="action-btn delete"
                                  title="Delete Category"
                                  onClick={() => handleDeleteCategory(cat)}
                                >
                                  <Trash2 size={15} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* TAB 2: GIFTS TAB */}
          {activeTab === 'gifts' && (
            <div className="admin-tab-pane">
              <div className="admin-filter-bar">
                <div className="admin-filter-dropdown-wrap">
                  <Filter size={16} className="filter-dropdown-icon" />
                  <label htmlFor="gift-occ-select" className="filter-label">Filter Occasion:</label>
                  <select
                    id="gift-occ-select"
                    className="admin-filter-select"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="All">All Occasions ({gifts.length})</option>
                    {OCCASIONS.map(occ => (
                      <option key={occ} value={occ}>{occ}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="admin-table-container">
                <table className="admin-data-table">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Gift Title</th>
                      <th>Marathi Title</th>
                      <th>Occasion</th>
                      <th>Recipient</th>
                      <th>Price (₹)</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredGifts.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="empty-table-cell">No gift items found matching criteria.</td>
                      </tr>
                    ) : (
                      paginatedGifts.map(g => (
                        <tr key={g.id}>
                          <td>
                            <img src={g.image || '/images/placeholder.jpg'} alt={g.name} className="table-thumb" />
                          </td>
                          <td><strong className="item-title">{g.name}</strong></td>
                          <td>{g.nameMr || '-'}</td>
                          <td><span className="cat-badge">{g.occasion}</span></td>
                          <td><span className="tag-badge">{g.forWhom}</span></td>
                          <td className="admin-price-cell"><strong className="admin-price-val" style={{ fontFamily: "'Poppins', sans-serif" }}>₹{Number(g.price).toLocaleString()}</strong></td>
                          <td>
                            <div className="table-actions">
                              <button className="action-btn edit" onClick={() => handleOpenEditGift(g)} title="Edit Gift">
                                <Edit3 size={15} />
                              </button>
                              <button className="action-btn delete" onClick={() => handleDeleteGift(g.id)} title="Delete Gift">
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Gifts Pagination Bar (10 per page) */}
              <AdminPagination
                currentPage={giftPage}
                totalPages={totalGiftPages}
                totalFilteredCount={filteredGifts.length}
                totalAllCount={gifts.length}
                itemsPerPage={GIFTS_PER_PAGE}
                itemName="gifts"
                onPageChange={setGiftPage}
              />
            </div>
          )}

          {/* TAB 3: BLOGS TAB */}
          {activeTab === 'blogs' && (
            <div className="admin-tab-pane">
              <div className="admin-table-container">
                <table className="admin-data-table">
                  <thead>
                    <tr>
                      <th>Cover Image</th>
                      <th>Blog Title</th>
                      <th>Marathi Title</th>
                      <th>Category</th>
                      <th>Author</th>
                      <th>Published Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBlogs.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="empty-table-cell">No blog posts found matching criteria.</td>
                      </tr>
                    ) : (
                      paginatedBlogs.map(b => (
                        <tr key={b.id}>
                          <td>
                            <img src={b.image || '/images/placeholder.jpg'} alt={b.title} className="table-thumb blog-thumb" />
                          </td>
                          <td><strong className="item-title">{b.title}</strong></td>
                          <td>{b.titleMr || '-'}</td>
                          <td><span className="cat-badge">{b.category || 'General'}</span></td>
                          <td>{b.author || 'GDR Editorial'}</td>
                          <td>{b.date || '-'}</td>
                          <td>
                            <div className="table-actions">
                              <button className="action-btn edit" onClick={() => handleOpenEditBlog(b)} title="Edit Blog Post">
                                <Edit3 size={15} />
                              </button>
                              <button className="action-btn delete" onClick={() => handleDeleteBlog(b.id)} title="Delete Blog Post">
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Blogs Pagination Bar (10 per page) */}
              <AdminPagination
                currentPage={blogPage}
                totalPages={totalBlogPages}
                totalFilteredCount={filteredBlogs.length}
                totalAllCount={blogs.length}
                itemsPerPage={BLOGS_PER_PAGE}
                itemName="blog posts"
                onPageChange={setBlogPage}
              />
            </div>
          )}

          {/* TAB 4: CONTACT INQUIRIES TAB */}
          {activeTab === 'inquiries' && (
            <div className="admin-tab-pane inquiries-tab-pane">
              {/* Status Filter & Summary Toolbar */}
              <div className="inquiry-filter-toolbar">
                <div className="inquiry-status-filters">
                  <span className="filter-label">Status:</span>
                  {[
                    { id: 'All', label: 'All Inquiries', count: inquiries.length },
                    { id: 'Follow Up', label: 'Follow Up', count: inquiries.filter(i => i.status === 'Follow Up').length },
                    { id: 'Interested', label: 'Interested', count: inquiries.filter(i => i.status === 'Interested').length },
                    { id: 'Not Interested', label: 'Not Interested', count: inquiries.filter(i => i.status === 'Not Interested').length },
                    { id: 'New', label: 'New', count: inquiries.filter(i => !i.status || i.status === 'New').length }
                  ].map(tab => (
                    <button
                      key={tab.id}
                      type="button"
                      className={`inquiry-filter-pill ${inquiryStatusFilter === tab.id ? 'active' : ''} ${tab.id.toLowerCase().replace(/\s+/g, '-')}`}
                      onClick={() => { setInquiryStatusFilter(tab.id); setInquiryPage(1); }}
                    >
                      <span>{tab.label}</span>
                      <span className="pill-badge">{tab.count}</span>
                    </button>
                  ))}
                </div>

                <div className="inquiry-page-quick-info">
                  <span>Page {inquiryPage} of {totalInquiryPages}</span>
                  <span className="inquiry-total-tag">({filteredInquiries.length} Total)</span>
                </div>
              </div>

              {/* Inquiries Table */}
              <div className="admin-table-container">
                <table className="admin-data-table inquiries-table">
                  <thead>
                    <tr>
                      <th style={{ width: '135px' }}>Date / Time</th>
                      <th style={{ width: '160px' }}>Customer Name</th>
                      <th style={{ width: '200px' }}>Contact Info</th>
                      <th>Subject & Message</th>
                      <th style={{ width: '135px' }}>Current Status</th>
                      <th style={{ width: '270px' }}>Mark Status</th>
                      <th style={{ width: '65px', textAlign: 'center' }}>Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedInquiries.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="empty-table-cell">
                          {inquiries.length === 0
                            ? 'No contact messages received yet.'
                            : 'No inquiries found matching selected filter or search.'}
                        </td>
                      </tr>
                    ) : (
                      paginatedInquiries.map(inq => {
                        const dateObj = parseInquiryDate(inq.createdAt);
                        const currentStatus = inq.status || 'New';

                        return (
                          <tr key={inq.id} className={`inquiry-row status-${currentStatus.toLowerCase().replace(/\s+/g, '-')}`}>
                            <td>
                              <div className="date-time-cell">
                                <span className="date-line">
                                  {dateObj ? dateObj.toLocaleDateString() : 'Recent'}
                                </span>
                                <span className="time-line">
                                  {dateObj ? dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                </span>
                              </div>
                            </td>
                            <td>
                              <strong className="inq-customer-name">{inq.name}</strong>
                            </td>
                            <td>
                              <div className="contact-meta-links">
                                {inq.email ? (
                                  <a href={`mailto:${inq.email}`} className="contact-meta-item email" title="Send Email">
                                    <Mail size={13} />
                                    <span>{inq.email}</span>
                                  </a>
                                ) : (
                                  <span className="contact-meta-item empty">No Email</span>
                                )}
                                {inq.phone ? (
                                  <a href={`tel:${inq.phone}`} className="contact-meta-item phone" title="Call Customer">
                                    <PhoneCall size={13} />
                                    <span>{inq.phone}</span>
                                  </a>
                                ) : (
                                  <span className="contact-meta-item empty">No Phone</span>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="inq-message-block">
                                <strong className="inq-subject-heading">{inq.subject || 'General Inquiry'}</strong>
                                <p className="inq-message-preview">{inq.message}</p>
                              </div>
                            </td>
                            <td>
                              {currentStatus === 'Follow Up' && (
                                <span className="inq-status-badge follow-up">
                                  <Clock size={12} /> Follow Up
                                </span>
                              )}
                              {currentStatus === 'Interested' && (
                                <span className="inq-status-badge interested">
                                  <ThumbsUp size={12} /> Interested
                                </span>
                              )}
                              {currentStatus === 'Not Interested' && (
                                <span className="inq-status-badge not-interested">
                                  <ThumbsDown size={12} /> Not Interested
                                </span>
                              )}
                              {currentStatus === 'New' && (
                                <span className="inq-status-badge new-lead">
                                  <Sparkles size={12} /> New
                                </span>
                              )}
                            </td>
                            <td>
                              <div className="inq-status-buttons-row">
                                <button
                                  type="button"
                                  className={`btn-lead-action follow-up ${currentStatus === 'Follow Up' ? 'active' : ''}`}
                                  onClick={() => handleUpdateInquiryStatus(inq.id, 'Follow Up')}
                                  title="Mark as Follow Up"
                                >
                                  <Clock size={12} />
                                  <span>Follow Up</span>
                                </button>
                                <button
                                  type="button"
                                  className={`btn-lead-action interested ${currentStatus === 'Interested' ? 'active' : ''}`}
                                  onClick={() => handleUpdateInquiryStatus(inq.id, 'Interested')}
                                  title="Mark as Interested"
                                >
                                  <ThumbsUp size={12} />
                                  <span>Interested</span>
                                </button>
                                <button
                                  type="button"
                                  className={`btn-lead-action not-interested ${currentStatus === 'Not Interested' ? 'active' : ''}`}
                                  onClick={() => handleUpdateInquiryStatus(inq.id, 'Not Interested')}
                                  title="Mark as Not Interested"
                                >
                                  <ThumbsDown size={12} />
                                  <span>Not Interested</span>
                                </button>
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                type="button"
                                className="action-btn delete"
                                onClick={() => handleDeleteInquiryItem(inq.id)}
                                title="Delete Message"
                              >
                                <Trash2 size={15} />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Inquiries Pagination Bar (15 per page) */}
              <AdminPagination
                currentPage={inquiryPage}
                totalPages={totalInquiryPages}
                totalFilteredCount={filteredInquiries.length}
                totalAllCount={inquiries.length}
                itemsPerPage={INQUIRIES_PER_PAGE}
                itemName="inquiries"
                onPageChange={setInquiryPage}
              />
            </div>
          )}

          {/* TAB 5: HOMEPAGE STATS MANAGEMENT */}
          {activeTab === 'stats' && (
            <div className="admin-tab-pane stats-management-pane">
              {/* Live Preview Box matching Home Page */}
              <div className="stats-live-preview-wrapper">
                <div className="stats-preview-title-bar">
                  <div className="stats-preview-title-left">
                    <Eye size={16} />
                    <span>REAL-TIME HOMEPAGE PREVIEW ({statsPreviewLang === 'en' ? 'English View' : 'मराठी दृश्य'})</span>
                  </div>
                  <div className="stats-preview-lang-toggle">
                    <span className="lang-toggle-label">Preview Mode:</span>
                    <div className="stats-lang-pills">
                      <button
                        type="button"
                        className={`stats-lang-pill ${statsPreviewLang === 'en' ? 'active' : ''}`}
                        onClick={() => setStatsPreviewLang('en')}
                      >
                        English
                      </button>
                      <button
                        type="button"
                        className={`stats-lang-pill ${statsPreviewLang === 'mr' ? 'active' : ''}`}
                        onClick={() => setStatsPreviewLang('mr')}
                      >
                        मराठी
                      </button>
                    </div>
                  </div>
                </div>
                <div className="stats-live-preview-content">
                  {statsForm.map((s, i) => {
                    const iconObj = STAT_ICONS.find(item => item.id === s.icon) || STAT_ICONS[0];
                    const IconC = iconObj.icon;
                    const labelText = statsPreviewLang === 'mr' ? (s.labelMr || s.label) : (s.label || s.labelMr);

                    return (
                      <div key={i} className="stats-preview-item">
                        <div className="stats-preview-icon">
                          <IconC size={28} />
                        </div>
                        <div className="stats-preview-number">
                          {s.target || 0}{s.suffix || ''}
                        </div>
                        <div className="stats-preview-label">
                          {labelText || 'Stat Label'}
                        </div>
                        {i < statsForm.length - 1 && <div className="stats-preview-divider" />}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 4 Cards Configuration Form */}
              <form onSubmit={handleSaveStats} className="stats-form-container">
                <div className="stats-cards-grid">
                  {statsForm.map((s, idx) => {
                    const currentIcon = STAT_ICONS.find(item => item.id === s.icon) || STAT_ICONS[0];
                    const IconComponent = currentIcon.icon;

                    return (
                      <div key={s.id || idx} className="stat-edit-box">
                        <div className="stat-box-header">
                          <div className="stat-box-badge">
                            <span className="stat-badge-num">0{idx + 1}</span>
                            <h4>Counter #{idx + 1}</h4>
                          </div>
                          <div className="stat-current-chip">
                            <IconComponent size={15} />
                            <span>{s.target || 0}{s.suffix || ''}</span>
                          </div>
                        </div>

                        <div className="stat-box-body">
                          <div className="form-row">
                            <div className="form-group" style={{ flex: '1.2' }}>
                              <label>Target Number *</label>
                              <input
                                type="number"
                                required
                                placeholder="e.g. 500"
                                value={s.target}
                                onChange={(e) => handleStatFieldChange(idx, 'target', e.target.value)}
                              />
                            </div>
                            <div className="form-group" style={{ flex: '0.8' }}>
                              <label>Suffix Symbol</label>
                              <input
                                type="text"
                                placeholder="e.g. + or %"
                                value={s.suffix}
                                onChange={(e) => handleStatFieldChange(idx, 'suffix', e.target.value)}
                              />
                            </div>
                          </div>

                          <div className="form-group">
                            <label>Label (English) *</label>
                            <input
                              type="text"
                              required
                              placeholder="e.g. Silver Designs"
                              value={s.label}
                              onChange={(e) => handleStatFieldChange(idx, 'label', e.target.value)}
                            />
                          </div>

                          <div className="form-group">
                            <label>Label (Marathi)</label>
                            <input
                              type="text"
                              placeholder="उदा. सिल्व्हर डिझाईन्स"
                              value={s.labelMr || ''}
                              onChange={(e) => handleStatFieldChange(idx, 'labelMr', e.target.value)}
                            />
                          </div>

                          <div className="form-group">
                            <label>Icon</label>
                            <div className="stat-icon-picker-grid">
                              {STAT_ICONS.map((opt) => {
                                const OptIcon = opt.icon;
                                const isSelected = s.icon === opt.id;
                                return (
                                  <button
                                    key={opt.id}
                                    type="button"
                                    className={`stat-icon-chip ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleStatFieldChange(idx, 'icon', opt.id)}
                                    title={opt.label}
                                  >
                                    <OptIcon size={14} />
                                    <span>{opt.id}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </form>
            </div>
          )}
        </main>
      </div>

      {/* --- MODAL DIALOGS FOR ADD / EDIT --- */}

      {/* 1. PRODUCT MODAL */}
      {(modalType === 'add-product' || modalType === 'edit-product') && (
        <div className="admin-modal-backdrop" onClick={closeModal}>
          <div className="admin-modal-box" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{modalType === 'add-product' ? 'Add New Jewellery Product' : 'Edit Product'}</h3>
              <button className="close-modal-btn" onClick={closeModal}><X size={18} /></button>
            </div>

            <form onSubmit={handleSaveProduct} className="admin-modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Product Name (English) *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Royal Silver Anklet"
                    value={productForm.name}
                    onChange={e => setProductForm({ ...productForm, name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Product Name (Marathi)</label>
                  <input
                    type="text"
                    placeholder="उदा. रॉयल चांदीची पायाळ"
                    value={productForm.nameMr}
                    onChange={e => setProductForm({ ...productForm, nameMr: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <div className="form-label-with-btn">
                    <label>Category *</label>
                    <button
                      type="button"
                      className="quick-add-cat-btn"
                      onClick={() => {
                        setCategoryForm({ name: '', nameMr: '', audience: 'For Women' });
                        setIsQuickCategoryModalOpen(true);
                      }}
                      title="Add a new category"
                    >
                      <Plus size={13} /> Add Category
                    </button>
                  </div>
                  <select
                    required
                    value={productForm.category}
                    onChange={e => handleProductCategoryChange(e.target.value)}
                  >
                    {categories.length === 0 && <option value="">No categories created yet</option>}
                    {categories.map(c => (
                      <option key={c.id || c.name} value={c.name}>
                        {c.name} {c.nameMr ? `(${c.nameMr})` : ''} {c.audience && c.audience !== 'Unisex / All' ? `— [${c.audience}]` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Sub Category / Audience</label>
                  <select
                    value={productForm.subCategory}
                    onChange={e => setProductForm({ ...productForm, subCategory: e.target.value })}
                  >
                    {TARGET_AUDIENCE.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Price (₹) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    placeholder="2500"
                    value={productForm.price}
                    onChange={e => setProductForm({ ...productForm, price: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Weight (grams)</label>
                  <input
                    type="number"
                    step="0.1"
                    placeholder="15.5"
                    value={productForm.weight}
                    onChange={e => setProductForm({ ...productForm, weight: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group image-upload-group">
                <label>Product Image *</label>
                <div className="image-input-wrapper">
                  <div className="file-upload-row">
                    <label htmlFor="product-file-picker" className="file-picker-btn">
                      <Upload size={16} />
                      <span>Choose Image File</span>
                    </label>
                    <input
                      id="product-file-picker"
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={e => handleImageFileUpload(e, setProductForm)}
                    />
                    <span className="upload-divider-text">OR paste Image URL:</span>
                  </div>
                  <input
                    type="text"
                    placeholder="Paste image URL (https://...) or click 'Choose Image File' above"
                    value={productForm.image}
                    onChange={e => setProductForm({ ...productForm, image: e.target.value })}
                    className="image-url-field"
                  />
                  {productForm.image && (
                    <div className="form-image-preview">
                      <img src={productForm.image} alt="Product Preview" />
                      <button
                        type="button"
                        className="remove-img-btn"
                        onClick={() => setProductForm({ ...productForm, image: '' })}
                      >
                        <X size={14} /> Clear Image
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Description (English)</label>
                <textarea
                  rows="3"
                  placeholder="Crafted from 925 Sterling Silver with intricate details..."
                  value={productForm.description}
                  onChange={e => setProductForm({ ...productForm, description: e.target.value })}
                ></textarea>
              </div>

              <div className="form-group">
                <label>Description (Marathi)</label>
                <textarea
                  rows="3"
                  placeholder="९२५ शुद्ध चांदीमध्ये तयार केलेले आकर्षक डिझाइन..."
                  value={productForm.descriptionMr}
                  onChange={e => setProductForm({ ...productForm, descriptionMr: e.target.value })}
                ></textarea>
              </div>

              <div className="form-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={productForm.inStock}
                    onChange={e => setProductForm({ ...productForm, inStock: e.target.checked })}
                  />
                  <span>Product is in Stock</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-primary"><span>Save Product</span></button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 2. GIFT MODAL */}
      {(modalType === 'add-gift' || modalType === 'edit-gift') && (
        <div className="admin-modal-backdrop" onClick={closeModal}>
          <div className="admin-modal-box" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{modalType === 'add-gift' ? 'Add Gift Item by Occasion' : 'Edit Gift Item'}</h3>
              <button className="close-modal-btn" onClick={closeModal}><X size={18} /></button>
            </div>

            <form onSubmit={handleSaveGift} className="admin-modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Gift Title (English) *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Silver Bridal Gift Set"
                    value={giftForm.name}
                    onChange={e => setGiftForm({ ...giftForm, name: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Gift Title (Marathi)</label>
                  <input
                    type="text"
                    placeholder="उदा. चांदीचा वाढदिवस भेट संच"
                    value={giftForm.nameMr}
                    onChange={e => setGiftForm({ ...giftForm, nameMr: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Occasion *</label>
                  <select
                    value={giftForm.occasion}
                    onChange={e => setGiftForm({ ...giftForm, occasion: e.target.value })}
                  >
                    {OCCASIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Recipient / For Whom *</label>
                  <select
                    value={giftForm.forWhom}
                    onChange={e => setGiftForm({ ...giftForm, forWhom: e.target.value })}
                  >
                    {RECIPIENTS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Price (₹) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    placeholder="3200"
                    value={giftForm.price}
                    onChange={e => setGiftForm({ ...giftForm, price: e.target.value })}
                  />
                </div>
                <div className="form-group image-upload-group">
                  <label>Gift Item Image *</label>
                  <div className="image-input-wrapper">
                    <div className="file-upload-row">
                      <label htmlFor="gift-file-picker" className="file-picker-btn">
                        <Upload size={16} />
                        <span>Choose Image File</span>
                      </label>
                      <input
                        id="gift-file-picker"
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={e => handleImageFileUpload(e, setGiftForm)}
                      />
                      <span className="upload-divider-text">OR paste Image URL:</span>
                    </div>
                    <input
                      type="text"
                      placeholder="Paste image URL (https://...) or click 'Choose Image File' above"
                      value={giftForm.image}
                      onChange={e => setGiftForm({ ...giftForm, image: e.target.value })}
                      className="image-url-field"
                    />
                    {giftForm.image && (
                      <div className="form-image-preview">
                        <img src={giftForm.image} alt="Gift Preview" />
                        <button
                          type="button"
                          className="remove-img-btn"
                          onClick={() => setGiftForm({ ...giftForm, image: '' })}
                        >
                          <X size={14} /> Clear Image
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Description (English)</label>
                <textarea
                  rows="3"
                  placeholder="Perfect silver gift curated for special celebrations..."
                  value={giftForm.description}
                  onChange={e => setGiftForm({ ...giftForm, description: e.target.value })}
                ></textarea>
              </div>

              <div className="form-group">
                <label>Description (Marathi)</label>
                <textarea
                  rows="3"
                  placeholder="विशेष प्रसंगांसाठी उत्कृष्ट चांदीची भेट..."
                  value={giftForm.descriptionMr}
                  onChange={e => setGiftForm({ ...giftForm, descriptionMr: e.target.value })}
                ></textarea>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-primary"><span>Save Gift Item</span></button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 3. BLOG MODAL */}
      {(modalType === 'add-blog' || modalType === 'edit-blog') && (
        <div className="admin-modal-backdrop" onClick={closeModal}>
          <div className="admin-modal-box" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{modalType === 'add-blog' ? 'Create New Blog Post' : 'Edit Blog Post'}</h3>
              <button className="close-modal-btn" onClick={closeModal}><X size={18} /></button>
            </div>

            <form onSubmit={handleSaveBlog} className="admin-modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Article Title (English) *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. How to Care for Pure 925 Silver"
                    value={blogForm.title}
                    onChange={e => setBlogForm({ ...blogForm, title: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Article Title (Marathi)</label>
                  <input
                    type="text"
                    placeholder="उदा. शुद्ध ९२५ चांदीची काळजी कशी घ्यावी"
                    value={blogForm.titleMr}
                    onChange={e => setBlogForm({ ...blogForm, titleMr: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={blogForm.category}
                    onChange={e => setBlogForm({ ...blogForm, category: e.target.value })}
                  >
                    {BLOG_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group image-upload-group">
                  <label>Cover Image *</label>
                  <div className="image-input-wrapper">
                    <div className="file-upload-row">
                      <label htmlFor="blog-file-picker" className="file-picker-btn">
                        <Upload size={16} />
                        <span>Choose Image File</span>
                      </label>
                      <input
                        id="blog-file-picker"
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={e => handleImageFileUpload(e, setBlogForm)}
                      />
                      <span className="upload-divider-text">OR paste Image URL:</span>
                    </div>
                    <input
                      type="text"
                      placeholder="Paste image URL (https://...) or click 'Choose Image File' above"
                      value={blogForm.image}
                      onChange={e => setBlogForm({ ...blogForm, image: e.target.value })}
                      className="image-url-field"
                    />
                    {blogForm.image && (
                      <div className="form-image-preview">
                        <img src={blogForm.image} alt="Blog Cover Preview" />
                        <button
                          type="button"
                          className="remove-img-btn"
                          onClick={() => setBlogForm({ ...blogForm, image: '' })}
                        >
                          <X size={14} /> Clear Image
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Excerpt / Summary (English)</label>
                <textarea
                  rows="2"
                  placeholder="Short summary displayed on the blog grid..."
                  value={blogForm.excerpt}
                  onChange={e => setBlogForm({ ...blogForm, excerpt: e.target.value })}
                ></textarea>
              </div>

              <div className="form-group">
                <label>Excerpt / Summary (Marathi)</label>
                <textarea
                  rows="2"
                  placeholder="ब्लॉग ग्रिडवर दिसणारा संक्षिप्त सारांश..."
                  value={blogForm.excerptMr}
                  onChange={e => setBlogForm({ ...blogForm, excerptMr: e.target.value })}
                ></textarea>
              </div>

              <div className="form-group">
                <label>Full Content (English) *</label>
                <textarea
                  rows="5"
                  required
                  placeholder="Detailed article body paragraphs..."
                  value={blogForm.content}
                  onChange={e => setBlogForm({ ...blogForm, content: e.target.value })}
                ></textarea>
              </div>

              <div className="form-group">
                <label>Full Content (Marathi)</label>
                <textarea
                  rows="5"
                  placeholder="सविस्तर ब्लॉग मजकूर..."
                  value={blogForm.contentMr}
                  onChange={e => setBlogForm({ ...blogForm, contentMr: e.target.value })}
                ></textarea>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-primary"><span>Save Blog Post</span></button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: ADD / EDIT CATEGORY */}
      {(modalType === 'add-category' || modalType === 'edit-category') && (
        <div className="admin-modal-backdrop" onClick={closeModal}>
          <div className="admin-modal-box cat-modal-box" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{modalType === 'add-category' ? 'Add New Product Category' : 'Edit Category'}</h3>
              <button className="close-modal-btn" onClick={closeModal}><X size={18} /></button>
            </div>

            <form onSubmit={handleSaveCategory} className="admin-modal-form">
              <div className="form-group">
                <label>Category Name (English) *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Payal (Anklets), Mangalsutra, Silver Coins"
                  value={categoryForm.name}
                  onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Category Name (Marathi) — मराठी नाव</label>
                <input
                  type="text"
                  placeholder="उदा. पैंजण (अँकलेट्स), मंगळसूत्र, चांदीची नाणी"
                  value={categoryForm.nameMr}
                  onChange={e => setCategoryForm({ ...categoryForm, nameMr: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Target Audience / Department *</label>
                <select
                  value={categoryForm.audience}
                  onChange={e => setCategoryForm({ ...categoryForm, audience: e.target.value })}
                >
                  {CATEGORY_AUDIENCES.map(aud => (
                    <option key={aud} value={aud}>{aud}</option>
                  ))}
                </select>
                <small className="form-hint">
                  This determines which section the category appears under in Products page filters.
                </small>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-primary">
                  <span>{modalType === 'add-category' ? 'Create Category' : 'Save Changes'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QUICK ADD CATEGORY MODAL (OVERLAY INSIDE ADD PRODUCT) */}
      {isQuickCategoryModalOpen && (
        <div className="admin-modal-backdrop quick-cat-overlay" onClick={() => setIsQuickCategoryModalOpen(false)}>
          <div className="admin-modal-box quick-cat-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Quick Add Product Category</h3>
              <button className="close-modal-btn" onClick={() => setIsQuickCategoryModalOpen(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleSaveQuickCategory} className="admin-modal-form">
              <div className="form-group">
                <label>Category Name (English) *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Mangalsutra"
                  value={categoryForm.name}
                  onChange={e => setCategoryForm({ ...categoryForm, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Category Name (Marathi) — मराठी नाव</label>
                <input
                  type="text"
                  placeholder="उदा. मंगळसूत्र"
                  value={categoryForm.nameMr}
                  onChange={e => setCategoryForm({ ...categoryForm, nameMr: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Target Audience / Department *</label>
                <select
                  value={categoryForm.audience}
                  onChange={e => setCategoryForm({ ...categoryForm, audience: e.target.value })}
                >
                  {CATEGORY_AUDIENCES.map(aud => (
                    <option key={aud} value={aud}>{aud}</option>
                  ))}
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setIsQuickCategoryModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-primary"><span>Save & Select</span></button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
